import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Compass } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { apiErrorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const AUTH_BG =
  "https://images.unsplash.com/photo-1580587771525-78b9dba3b914?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHwxfHxwcmVtaXVtJTIwbW9kZXJuJTIwcmVhbCUyMGVzdGF0ZSUyMGFyY2hpdGVjdHVyZXxlbnwwfHx8fDE3ODQzOTkxNjV8MA&ixlib=rb-4.1.0&q=85";

export default function Register() {
  const { register } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(name, email, password);
      navigate("/dashboard");
    } catch (err) {
      toast.error(apiErrorMessage(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="hidden lg:block relative">
        <img src={AUTH_BG} alt="Architecture" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-black/55 grain-overlay" />
        <div className="absolute bottom-0 p-12">
          <Compass className="h-7 w-7 text-[hsl(var(--success))] mb-6" />
          <h2 className="font-display text-4xl text-white leading-tight max-w-md">{t("hero_line")}</h2>
          <p className="text-white/60 mt-4 text-sm max-w-sm">YieldPilot · Portugal → Europe</p>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 lg:p-12">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-sm">
          <div className="flex items-center gap-2 mb-8 lg:hidden">
            <Compass className="h-6 w-6 text-[hsl(var(--success))]" />
            <span className="font-display text-2xl">YieldPilot</span>
          </div>
          <h1 className="font-display text-3xl mb-1">{t("register_title")}</h1>
          <p className="text-muted-foreground text-sm mb-8">{t("register_sub")}</p>

          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">{t("name")}</Label>
              <Input id="name" required value={name} onChange={(e) => setName(e.target.value)}
                data-testid="register-name" className="rounded-sm" placeholder="Maria Silva" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">{t("email")}</Label>
              <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                data-testid="register-email" className="rounded-sm" placeholder="you@email.com" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">{t("password")}</Label>
              <Input id="password" type="password" required minLength={6} value={password}
                onChange={(e) => setPassword(e.target.value)} data-testid="register-password"
                className="rounded-sm" placeholder="••••••••" />
            </div>
            <Button type="submit" disabled={loading} data-testid="register-submit"
              className="w-full rounded-sm active:scale-95 transition-transform">
              {loading ? "..." : t("register")}
            </Button>
          </form>

          <p className="text-sm text-muted-foreground mt-8 text-center">
            {t("have_account")}{" "}
            <Link to="/login" data-testid="go-login" className="text-foreground underline underline-offset-4">
              {t("login")}
            </Link>
          </p>
        </motion.div>
      </div>
    </div>
  );
}
